/**
 * Funding Options Page
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FundingFinder from "../components/FundingFinder";
import {
  CheckCircle, ArrowRight, ChevronDown,
  Building2, Wrench, TrendingUp, CreditCard, DollarSign, Briefcase, Banknote
} from "lucide-react";

const HERO_BG = "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/g6m4dm0x_businessmen-shaking-hands.jpg";

const PRODUCTS = [
  {
    id: "credit-card-stacking",
    icon: CreditCard,
    label: "Fastest Funding",
    labelColor: "bg-[#06b6d4] text-white",
    name: "Business Credit Cards",
    tagline: "Access multiple business credit cards for immediate working capital",
    amount: "$5K – $100K+",
    rate: "0% – 21%",
    term: "Revolving",
    time: "24 – 48 hours",
    description: "Business credit cards strategically combine multiple cards to access significant working capital quickly.",
    highlights: [
      "Access $5K–$100K+ in immediate credit",
      "Multiple 0% APR intro periods",
      "Approved and funded within 24–48 hours",
      "No collateral required",
      "Builds business credit",
    ],
    color: "#06b6d4",
  },
  {
    id: "sba",
    icon: Building2,
    label: "Most Popular",
    labelColor: "bg-[#48d480] text-[#0f2d52]",
    name: "SBA Loans",
    tagline: "Government-backed financing with the lowest rates available",
    amount: "$50K – $5M",
    rate: "6.5% – 10.5%",
    term: "10 – 25 years",
    time: "30 – 90 days",
    description: "SBA loans are government-backed financing options that offer the lowest interest rates and longest repayment terms available to small businesses.",
    highlights: [
      "Lowest interest rates in the market",
      "Longest repayment terms (up to 25 years)",
      "Can be used for real estate, equipment, and working capital",
      "Government guarantee reduces lender risk",
    ],
    color: "#48d480",
  },
  {
    id: "equipment",
    icon: Wrench,
    label: "Fast Approval",
    labelColor: "bg-[#e8821c] text-white",
    name: "Equipment Financing",
    tagline: "Finance the tools your business needs to grow",
    amount: "$10K – $2M",
    rate: "5% – 15%",
    term: "2 – 7 years",
    time: "2 – 5 days",
    description: "Equipment financing allows you to purchase or lease the machinery, vehicles, technology, or tools your business needs.",
    highlights: [
      "Equipment serves as its own collateral",
      "Preserve working capital for operations",
      "Tax advantages (Section 179 deduction)",
      "Finance new or used equipment",
    ],
    color: "#e8821c",
  },
  {
    id: "line-of-credit",
    icon: TrendingUp,
    label: "Flexible",
    labelColor: "bg-[#8b5cf6] text-white",
    name: "Line of Credit",
    tagline: "Flexible credit access for ongoing business needs",
    amount: "$5K – $250K",
    rate: "7% – 18%",
    term: "Revolving",
    time: "3 – 7 days",
    description: "A business line of credit provides flexible access to funds whenever you need them.",
    highlights: [
      "Only pay interest on what you use",
      "Flexible draw and repayment schedule",
      "Funds available on-demand",
      "Revolving credit that renews",
    ],
    color: "#8b5cf6",
  },
  {
    id: "working-capital",
    icon: DollarSign,
    label: "Fast Cash",
    labelColor: "bg-[#3b82f6] text-white",
    name: "Working Capital",
    tagline: "Keep your business running smoothly with fast cash flow",
    amount: "$5K – $500K",
    rate: "8% – 35%",
    term: "3 – 24 months",
    time: "24 – 72 hours",
    description: "Working capital loans provide fast access to funds for day-to-day business operations.",
    highlights: [
      "Funding in as little as 24 hours",
      "Minimal documentation required",
      "No collateral required for most programs",
      "Flexible repayment terms",
    ],
    color: "#3b82f6",
  },
  {
    id: "business-credit-builder",
    icon: Briefcase,
    label: "Build Credit",
    labelColor: "bg-[#10b981] text-white",
    name: "Business Credit Builder",
    tagline: "Build strong business credit and unlock better funding options",
    amount: "$5K – $100K+",
    rate: "Varies",
    term: "Ongoing",
    time: "Immediate Start",
    description: "Our Business Credit Builder program helps you establish and build a strong EIN-based business credit profile, separating personal and business finances.",
    highlights: [
      "Build credit in your business EIN",
      "Access to 1,000+ lender partners",
      "Step-by-step credit building guidance",
      "Fundability Score™ monitoring",
      "No personal guarantee required",
    ],
    color: "#10b981",
  },
  {
    id: "mca",
    icon: Banknote,
    label: "Quick Funding",
    labelColor: "bg-[#f59e0b] text-white",
    name: "Merchant Cash Advance",
    tagline: "Fast funding based on your future sales",
    amount: "$5K – $500K",
    rate: "Factor Rate 1.1 – 1.5",
    term: "3 – 18 months",
    time: "24 – 48 hours",
    description: "A Merchant Cash Advance provides quick access to capital based on your business's future credit card sales or revenue.",
    highlights: [
      "Funding in as little as 24 hours",
      "Flexible repayment based on sales",
      "No fixed monthly payments",
      "Bad credit accepted",
      "No collateral required",
    ],
    color: "#f59e0b",
  },
];

const FAQS = [
  { q: "How do I know which funding option is right for my business?", a: "Use our Eligibility Calculator above to see which products you qualify for based on your business profile. Or schedule a free consultation with our advisors." },
  { q: "Can I apply for multiple funding products at the same time?", a: "Yes! Many businesses use a combination of funding products. Our advisors can help you structure a comprehensive funding strategy." },
  { q: "Does applying affect my credit score?", a: "Our initial pre-qualification process uses a soft credit pull, which does not affect your credit score." },
  { q: "How long does the application process take?", a: "It varies by product. Business Credit Cards can fund in 24–48 hours. SBA loans require 30–90 days." },
];

export default function FundingOptions() {
  const [activeProduct, setActiveProduct] = useState("credit-card-stacking");
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selected = PRODUCTS.find((p) => p.id === activeProduct);

  return (
    <div className="min-h-screen bg-white" data-testid="funding-options-page">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] overflow-hidden pt-32 md:pt-40 flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-[#0f2d52]/70" />
        
        <div className="container relative z-10 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#48d480]/20 border border-[#48d480]/40 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#48d480] animate-pulse" />
              <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#48d480] text-xs font-semibold tracking-wide">
                Multiple Funding Options Available
              </span>
            </div>
            
            <h1 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 sm:mb-6 leading-tight">
              Find Your Perfect
              <br />
              <span className="text-[#48d480]">Funding Solution</span>
            </h1>
            
            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-6 sm:mb-8 leading-relaxed px-2">
              From instant credit card stacking to government-backed SBA loans, we have the right financing for your business. Access $5K to $5M+ in funding.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 px-4 sm:px-0">
              <a href="#funding-finder" className="btn-outline-white flex items-center justify-center gap-2 text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4">
                Check My Eligibility <ArrowRight size={18} />
              </a>
              <a href="https://app.trulli.ai/marketplace/legacytriangle" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 bg-[#e8821c] hover:bg-[#d67518] text-white font-bold rounded-lg transition-colors">
                Apply Now
              </a>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 max-w-2xl mx-auto px-2 sm:px-0">
              {[
                { value: "5+", label: "Funding Types" },
                { value: "$5M+", label: "Max Amount" },
                { value: "24hrs", label: "Fast Funding" },
                { value: "100+", label: "Lender Partners" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-2 sm:p-3 border border-white/10">
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-white font-black text-lg sm:text-xl">{stat.value}</div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-[10px] sm:text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Funding Finder */}
      <FundingFinder />

      {/* Product Tabs */}
      <section className="py-12 sm:py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-[#0f2d52]">
            Our Funding Products
          </h2>

          {/* Tab Navigation - Horizontal scroll on mobile */}
          <div className="overflow-x-auto pb-4 mb-8 sm:mb-12 -mx-4 px-4">
            <div className="flex gap-2 sm:flex-wrap sm:justify-center min-w-max sm:min-w-0">
              {PRODUCTS.map((product) => (
                <button
                  key={product.id}
                  onClick={() => setActiveProduct(product.id)}
                  data-testid={`product-tab-${product.id}`}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-semibold transition-all text-sm sm:text-base whitespace-nowrap ${
                    activeProduct === product.id
                      ? "bg-[#0f2d52] text-white"
                      : "bg-white text-[#0f2d52] border-2 border-[#0f2d52] hover:bg-gray-100"
                  }`}
                >
                  {product.name}
                </button>
              ))}
            </div>
          </div>

          {/* Product Detail Card */}
          {selected && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="product-detail">
              <div className="grid md:grid-cols-2 gap-6 sm:gap-8 p-5 sm:p-8">
                <div>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs sm:text-sm font-bold ${selected.labelColor} mb-3 sm:mb-4`}>
                    {selected.label}
                  </span>
                  <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-2xl sm:text-3xl font-bold text-[#0f2d52] mb-2">{selected.name}</h3>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm mb-3 sm:mb-4">{selected.tagline}</p>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-700 text-sm sm:text-base mb-5 sm:mb-6">{selected.description}</p>

                  {/* Key Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 uppercase">Funding Amount</p>
                      <p className="text-lg font-bold text-[#0f2d52]">{selected.amount}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 uppercase">Interest Rate</p>
                      <p className="text-lg font-bold text-[#0f2d52]">{selected.rate}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 uppercase">Term</p>
                      <p className="text-lg font-bold text-[#0f2d52]">{selected.term}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 uppercase">Funding Speed</p>
                      <p className="text-lg font-bold text-[#0f2d52]">{selected.time}</p>
                    </div>
                  </div>

                  <a href="https://app.trulli.ai/marketplace/legacytriangle" target="_blank" rel="noopener noreferrer">
                    <button className="w-full bg-[#e8821c] hover:bg-[#d67518] text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2">
                      Apply Now <ArrowRight size={20} />
                    </button>
                  </a>
                </div>

                <div className="bg-gray-50 p-8 rounded-xl">
                  <h4 style={{ fontFamily: "'Montserrat', sans-serif" }} className="font-bold text-[#0f2d52] mb-4">Key Benefits</h4>
                  <ul className="space-y-3">
                    {selected.highlights.map((highlight, idx) => (
                      <li key={idx} className="flex gap-3">
                        <CheckCircle size={20} className="text-[#48d480] flex-shrink-0 mt-0.5" />
                        <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-700">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-4xl font-bold text-center mb-12 text-[#0f2d52]">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {FAQS.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-gray-200">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span style={{ fontFamily: "'Inter', sans-serif" }} className="font-semibold text-[#0f2d52] text-left">{faq.q}</span>
                  <ChevronDown size={20} className={`text-[#48d480] transition-transform ${openFaq === idx ? "rotate-180" : ""}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-4 text-gray-700 border-t" style={{ fontFamily: "'Inter', sans-serif" }}>{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-[#0f2d52] text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-4xl font-bold mb-4">Ready to Get Funded?</h2>
          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-lg mb-8 text-gray-200">
            Schedule a free consultation with one of our funding specialists today.
          </p>
          <Link to="/contact">
            <button className="bg-[#48d480] text-[#0f2d52] font-bold px-8 py-4 rounded-lg hover:bg-[#3ab86a] transition-all text-lg">
              Book Your Free Consultation
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
